"use client";

/**
 * AlertCellOutputsView — modal Output tab.
 *
 * Carousel with inner tabs, priority order (highest → lowest):
 *   1. Dashboard (dashboard)          — hero HTML dashboard; lands FIRST when present
 *   2. Search  (unified_search)       — News / Twitter / Reddit
 *   3. Chart   (plotly / bokeh / backtest-tabs / image)
 *   4. Data    (html tables)
 *   5. Analysis (fund_agent / sub_agent)
 *   6. Stdout  (text)
 *   7. Errors  (error)
 *
 * Only ONE output category is visible at a time. Tab bar shows counts.
 * Mirrors NotebookCell's renderers via ChartsTab / UnifiedSearchCarousel /
 * AgentResultsCarousel — zero new renderers invented here. Dashboards reuse
 * ChartsTab's dispatch (which already routes type==="dashboard" to
 * DashboardView), just filtered to the dashboard outputs only.
 */

import { useMemo, useState, useEffect } from "react";
import {
  TerminalSquare,
  AlertTriangle,
  Newspaper,
  BarChart3,
  Table,
  Brain,
  LayoutDashboard,
  Activity,
} from "lucide-react";
import { ChartsTab } from "@/components/notebook/tabs/ChartsTab";
import { UnifiedSearchCarousel } from "@/components/notebook/UnifiedSearchCarousel";
import { SignalRegistrationCard } from "@/components/notebook/SignalRegistrationCard";
import { AgentResultsCarousel } from "@/components/notebook/AgentResultsCarousel";
import { cn } from "@/lib/utils";
import { SUB_CLASS } from "@/lib/alert-theme";
import {
  isDashboardOutput,
  isNoiseStdoutText,
  sortOutputsByVisualPriority,
} from "@/lib/alert-output-detection";
import { alertLog, summarizeOutput } from "@/lib/alert-display-log";

/* eslint-disable @typescript-eslint/no-explicit-any */

interface RawOutput {
  type: string;
  data: any;
}

interface AlertCellOutputsViewProps {
  outputs?: RawOutput[];
  /** Legacy stdout string fallback. */
  stdout?: string;
  viewMode?: string;
}

type TabKey =
  | "signal"
  | "dashboard"
  | "search"
  | "chart"
  | "data"
  | "analysis"
  | "stdout"
  | "error";

interface TabSpec {
  key: TabKey;
  label: string;
  count: number;
  Icon: typeof Newspaper;
}

export function AlertCellOutputsView({
  outputs,
  stdout,
  viewMode,
}: AlertCellOutputsViewProps) {
  const safe = useMemo<RawOutput[]>(
    () => (Array.isArray(outputs) ? outputs : []),
    [outputs],
  );

  const unifiedSearch = useMemo(
    () => safe.filter((o) => o.type === "unified_search").map((o) => o.data),
    [safe],
  );

  // Signal-registration cards are the hero output on a signal canvas — they
  // get the first tab (ahead of dashboard) so expanding the final node lands
  // straight on "what fired this run".
  const signalRegister = useMemo(
    () => safe.filter((o) => o.type === "signal_register").map((o) => o.data),
    [safe],
  );

  // Dashboards get their own dedicated tab (shown FIRST when present) so the
  // hero alert visual is what the user lands on when expanding a cell.
  //
  // Dashboards can arrive as either:
  //   - type === "dashboard" (already normalized), OR
  //   - type === "html" with marker `data-xynth-component="dashboard"` inside
  //     (raw notebook output that never got normalized through the
  //     tool-display pipeline — common on the active-run-details endpoint).
  // We normalize the latter into the shape ChartsTab / DashboardView expect
  // (unwrap the wrapper div and flip type to "dashboard") so the rendering
  // path is identical for both shapes.
  const dashboardOutputs = useMemo(() => {
    return safe.filter(isDashboardOutput).map((o) => {
      if (o.type === "dashboard") return o;
      const raw = String(o.data ?? "");
      const innerMatch = raw.match(
        /<div\s+data-xynth-component="dashboard">([\s\S]*)<\/div>\s*$/i,
      );
      return {
        type: "dashboard",
        data: innerMatch ? innerMatch[1] : raw,
      } as RawOutput;
    });
  }, [safe]);

  const chartOutputs = useMemo(() => {
    const isChartLikeHtml = (o: RawOutput) => {
      if (o.type !== "html" || typeof o.data !== "string") return false;
      const n = o.data.replace(/\s+/g, " ").trim().toLowerCase();
      return (
        n.includes('data-visualization-type="backtest-tabs"') &&
        n.includes('data-xynth-component="true"')
      );
    };
    // Filter first, then reorder so interactive charts (plotly/bokeh/
    // backtest-tabs) lead and static PNGs trail. Without this sort the
    // Chart tab preserves raw notebook order — if the agent emits an
    // image snapshot before its real plotly chart, the PNG wins and the
    // interactive chart is buried. The visual-priority helper treats
    // plotly=1, backtest-tabs-html=2, image=3 so we get the right order.
    const filtered = safe.filter((o) => {
      if (isDashboardOutput(o)) return false; // dashboards own their own tab
      if (o.type === "plotly") return true;
      if (o.type === "bokeh") return true;
      if (o.type === "backtest-tabs") return true;
      if (o.type === "image") return true;
      return isChartLikeHtml(o);
    });
    return sortOutputsByVisualPriority(filtered) ?? filtered;
  }, [safe]);

  const dataOutputs = useMemo(() => {
    return safe.filter((o) => {
      if (isDashboardOutput(o)) return false; // don't double-count dashboards
      if (o.type !== "html" || typeof o.data !== "string") return false;
      // Skip chart-like html (already captured above).
      const n = o.data.replace(/\s+/g, " ").trim().toLowerCase();
      if (
        n.includes('data-visualization-type="backtest-tabs"') &&
        n.includes('data-xynth-component="true"')
      )
        return false;
      return /<table/i.test(o.data);
    });
  }, [safe]);

  const agentResults = useMemo(() => {
    const arr: Array<{ type: "fund_agent" | "sub_agent"; data: any }> = [];
    for (const o of safe) {
      if (o.type === "fund_agent") arr.push({ type: "fund_agent", data: o.data });
      else if (o.type === "sub_agent")
        arr.push({ type: "sub_agent", data: o.data });
    }
    return arr;
  }, [safe]);

  const textBlocks = useMemo(
    () =>
      safe
        .filter(
          (o) =>
            o.type === "text" &&
            typeof o.data === "string" &&
            // Skip Plotly placeholder text so the Print tab count and
            // content never include "[Plotly Figure]" noise lines.
            !isNoiseStdoutText(o.data),
        )
        .map((o) => o.data as string),
    [safe],
  );

  const errorBlocks = useMemo(
    () => safe.filter((o) => o.type === "error"),
    [safe],
  );

  // Build tab list in priority order, keeping only tabs that have content.
  const tabs = useMemo<TabSpec[]>(() => {
    const result: TabSpec[] = [];
    if (signalRegister.length > 0) {
      result.push({
        key: "signal",
        label: "Signal",
        count: signalRegister.reduce(
          (sum, s) => sum + (Array.isArray(s?.positions) ? s.positions.length : 0),
          0,
        ) || signalRegister.length,
        Icon: Activity,
      });
    }
    if (dashboardOutputs.length > 0) {
      result.push({
        key: "dashboard",
        label: "Dashboard",
        count: dashboardOutputs.length,
        Icon: LayoutDashboard,
      });
    }
    if (unifiedSearch.length > 0) {
      const totalSources = unifiedSearch.reduce(
        (sum, s) => sum + (Array.isArray(s?.sources) ? s.sources.length : 0),
        0,
      );
      result.push({
        key: "search",
        label: "Search",
        count: totalSources || unifiedSearch.length,
        Icon: Newspaper,
      });
    }
    if (chartOutputs.length > 0) {
      result.push({
        key: "chart",
        label: "Chart",
        count: chartOutputs.length,
        Icon: BarChart3,
      });
    }
    if (dataOutputs.length > 0) {
      result.push({
        key: "data",
        label: "Data",
        count: dataOutputs.length,
        Icon: Table,
      });
    }
    if (agentResults.length > 0) {
      result.push({
        key: "analysis",
        label: "Analysis",
        count: agentResults.length,
        Icon: Brain,
      });
    }
    if (textBlocks.length > 0 || (stdout && stdout.trim().length > 0)) {
      result.push({
        key: "stdout",
        label: "Print",
        count: textBlocks.length || 1,
        Icon: TerminalSquare,
      });
    }
    if (errorBlocks.length > 0) {
      result.push({
        key: "error",
        label: "Error",
        count: errorBlocks.length,
        Icon: AlertTriangle,
      });
    }
    return result;
  }, [
    signalRegister,
    dashboardOutputs,
    unifiedSearch,
    chartOutputs,
    dataOutputs,
    agentResults,
    textBlocks,
    errorBlocks,
    stdout,
  ]);

  const [activeKey, setActiveKey] = useState<TabKey | null>(
    tabs[0]?.key ?? null,
  );
  useEffect(() => {
    // If the active tab disappears (outputs changed), reset to the first one.
    if (!tabs.find((t) => t.key === activeKey)) {
      setActiveKey(tabs[0]?.key ?? null);
    }
  }, [tabs, activeKey]);

  // Log the assembled tab bar + which tab lands as active. This is the
  // clearest signal for "is dashboard actually promoted to the first tab?".
  useEffect(() => {
    alertLog("modal:tabs_built", {
      total_outputs: safe.length,
      outputs: safe.map(summarizeOutput),
      tabs: tabs.map((t) => ({ key: t.key, count: t.count })),
      first_tab: tabs[0]?.key ?? null,
      active_tab: activeKey,
      dashboard_tab_count: dashboardOutputs.length,
      chart_tab_count: chartOutputs.length,
      data_tab_count: dataOutputs.length,
      search_tab_count: unifiedSearch.length,
      analysis_tab_count: agentResults.length,
      error_tab_count: errorBlocks.length,
    });
  }, [
    tabs,
    activeKey,
    safe,
    dashboardOutputs.length,
    chartOutputs.length,
    dataOutputs.length,
    unifiedSearch.length,
    agentResults.length,
    errorBlocks.length,
  ]);

  if (tabs.length === 0) {
    return (
      <div className="flex items-center gap-3 rounded-lg border border-slate-400/15 bg-slate-900/30 px-4 py-3">
        <TerminalSquare className="w-4 h-4 text-slate-300 shrink-0" />
        <div className={cn(SUB_CLASS, "text-slate-100/85")}>
          No output yet — this step will populate once the alert runs.
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {/* Tab bar — only render if there's more than one category. */}
      {tabs.length > 1 && (
        <div className="flex items-center flex-wrap gap-1.5">
          {tabs.map((t) => {
            const active = t.key === activeKey;
            const isError = t.key === "error";
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => {
                  alertLog("modal:tab_click", {
                    from: activeKey,
                    to: t.key,
                    count: t.count,
                  });
                  setActiveKey(t.key);
                }}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-[11px] font-medium border transition-colors",
                  active
                    ? isError
                      ? "bg-red-400/15 border-red-300/40 text-red-100"
                      : viewMode === "signal" ? "bg-emerald-300/15 border-emerald-300/45 text-emerald-50" : "bg-sky-300/15 border-sky-300/45 text-sky-50"
                    : "bg-slate-100/[0.03] border-slate-400/20 text-slate-300/85 hover:text-slate-50 hover:border-slate-300/40",
                )}
              >
                <t.Icon className="w-3 h-3" />
                {t.label}
                <span
                  className={cn(
                    "inline-flex items-center justify-center min-w-[18px] h-[15px] rounded-full text-[10px] font-semibold tabular-nums px-1",
                    active
                      ? isError
                        ? "bg-red-400/25 text-red-50"
                        : viewMode === "signal" ? "bg-emerald-300/25 text-emerald-50" : "bg-sky-300/25 text-sky-50"
                      : "bg-slate-100/[0.06] text-slate-200",
                  )}
                >
                  {t.count}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {/* Active tab content. */}
      <div className="min-h-[120px]">
        {activeKey === "signal" && signalRegister.length > 0 && (
          <div className="flex flex-col gap-3">
            {signalRegister.map((d, i) => (
              <SignalRegistrationCard key={i} data={d} />
            ))}
          </div>
        )}

        {activeKey === "dashboard" && dashboardOutputs.length > 0 && (
          <ChartsTab chartOutputs={dashboardOutputs as any} isReplay={false} />
        )}

        {activeKey === "search" && unifiedSearch.length > 0 && (
          <UnifiedSearchCarousel results={unifiedSearch as any} />
        )}

        {activeKey === "chart" && chartOutputs.length > 0 && (
          <ChartsTab chartOutputs={chartOutputs as any} isReplay={false} />
        )}

        {activeKey === "data" && dataOutputs.length > 0 && (
          <div className="rounded-lg border border-slate-400/15 bg-slate-900/50 overflow-hidden">
            <div className="overflow-auto max-h-[60vh] p-3 text-[12px] text-slate-100 alert-data-table">
              {dataOutputs.map((o, i) => (
                <div
                  key={i}
                  className={i > 0 ? "mt-3 pt-3 border-t border-slate-400/15" : ""}
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{ __html: String(o.data) }}
                />
              ))}
            </div>
            <style jsx>{`
              .alert-data-table :global(table) {
                border-collapse: collapse;
                width: 100%;
              }
              .alert-data-table :global(th),
              .alert-data-table :global(td) {
                border: 1px solid rgba(148, 163, 184, 0.2);
                padding: 4px 8px;
                text-align: left;
              }
              .alert-data-table :global(th) {
                background: rgba(186, 230, 253, 0.06);
                color: rgb(224 242 254);
                font-weight: 600;
              }
              .alert-data-table :global(tr:nth-child(even)) {
                background: rgba(148, 163, 184, 0.04);
              }
            `}</style>
          </div>
        )}

        {activeKey === "analysis" && agentResults.length > 0 && (
          <AgentResultsCarousel results={agentResults as any} />
        )}

        {activeKey === "stdout" && (
          <div className="rounded-lg border border-slate-400/15 bg-slate-900/50 overflow-hidden">
            <pre className="p-3 whitespace-pre-wrap break-words font-mono text-[11.5px] leading-[1.55] text-slate-100">
              {textBlocks.length > 0
                ? textBlocks.join("\n\n")
                : (stdout || "")}
            </pre>
          </div>
        )}

        {activeKey === "error" && errorBlocks.length > 0 && (
          <div className="flex flex-col gap-2">
            {errorBlocks.map((o, i) => (
              <div
                key={`err-${i}`}
                className="flex items-start gap-3 rounded-lg border border-red-500/30 bg-red-500/[0.06] px-3 py-2.5"
              >
                <AlertTriangle className="w-4 h-4 text-red-300 mt-0.5 shrink-0" />
                <pre className="font-mono text-[11px] leading-snug text-red-100/90 whitespace-pre-wrap break-words">
                  {typeof o.data === "string"
                    ? o.data
                    : JSON.stringify(o.data, null, 2)}
                </pre>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
